import React, { useContext, useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormik, Form, FormikProvider } from 'formik';
import { Button, Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import TicketFormBasic from '../basic';
// contexts
import { EventContext } from '../../../../contexts/Event';
import { TicketsContext } from '../../../../contexts/Tickets';
import { useAuth } from '../../../../contexts/Auth';
import { SettingsContext } from '../../../../contexts/Settings';
// services
import { updateTicket } from '../../../../services/Ticket';
import { updateSection } from '../../../../services/Section';
import { updateInventoryMany } from '../../../../services/Inventory';

TicketFormEditDialog.propTypes = {
  openTicketFormEditDialog: PropTypes.bool.isRequired,
  setOpenTicketFormEditDialog: PropTypes.func.isRequired,
  ticket: PropTypes.object
};

export default function TicketFormEditDialog({
  openTicketFormEditDialog,
  setOpenTicketFormEditDialog,
  ticket
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);
  const { setTickets } = useContext(TicketsContext);

  const initialTicketValues = useMemo(
    () => ({
      sectionId: '',
      name: '',
      description: '',
      reference: '',
      limit: '',
      price: '',
      translation: {
        pt: {
          name: '',
          description: ''
        }
      }
    }),
    []
  );

  const [ticketValues, setTicketValues] = useState(initialTicketValues);

  useEffect(() => {
    if (ticket?._id) {
      setTicketValues((currentTicket) => ({
        ...currentTicket,
        sectionId: ticket.section._id,
        name: ticket.name,
        description: ticket.description,
        reference: ticket.reference,
        limit: ticket.limit,
        price: ticket.price,
        translation: ticket.translation
      }));
    } else {
      setTicketValues(initialTicketValues);
    }
  }, [ticket, initialTicketValues]);

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const TicketSchema = Yup.object().shape({
    sectionId: Yup.string().required('Section is required'),
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    reference: Yup.string().required('Reference is required'),
    limit: Yup.number().integer().positive().required('Limit is required'),
    price: Yup.number()
      .moreThan(-1, 'Price must be a positive number')
      .required('Price is required'),
    translation: Yup.object({
      pt: Yup.object({
        name: Yup.string(),
        description: Yup.string()
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ticketValues,
    validationSchema: TicketSchema,
    onSubmit: async (values, actions) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        if (ticket.section.single) {
          const payloadSection = {
            capacity: values.limit,
            event: {
              _id: event._id,
              name: event.name
            }
          };

          await updateSection({
            headers,
            payload: payloadSection,
            eventId: event._id,
            sectionId: ticket.section._id
          });
        }

        const payload = {
          name: values.name,
          description: values.description,
          reference: values.reference,
          limit: values.limit,
          price: values.price,
          section: ticket.section,
          event: {
            _id: event._id,
            name: event.name,
            calendar: event.calendar,
            translation: {
              pt: {
                name: event.translation.pt.name
              }
            }
          },
          translation: {
            pt: {
              name: values.translation.pt.name,
              description: values.translation.pt.description
            }
          }
        };

        await updateTicket({
          headers,
          payload,
          eventId: event._id,
          sectionId: ticket.section._id,
          ticketId: ticket._id
        });

        setTickets((tickets) => {
          tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
            if (currentTicket._id === ticket._id) {
              return {
                ...ticket,
                name: values.name,
                description: values.description,
                reference: values.reference,
                limit: values.limit,
                price: values.price,
                section: ticket.section,
                translation: {
                  pt: {
                    name: values.translation.pt.name,
                    description: values.translation.pt.description
                  }
                }
              };
            }

            return currentTicket;
          });

          return tickets;
        });

        // TODO: Atualizar usando kafka (provisório)
        const payloadServices = {
          ticket: {
            _id: ticket._id,
            name: values.name,
            description: values.description,
            reference: values.reference,
            limit: values.limit,
            price: values.price,
            translation: {
              pt: {
                name: values.translation.pt.name,
                description: values.translation.pt.description
              }
            }
          }
        };

        await updateInventoryMany({ headers, payload: payloadServices });

        if (ticket.section.single) {
          const payloadServicesSection = {
            section: {
              _id: ticket.section._id,
              name: ticket.section.name,
              description: ticket.section.description,
              single: ticket.section.single,
              capacity: values.limit
            }
          };

          await updateInventoryMany({ headers, payload: payloadServicesSection });
        }

        handleClose();
        actions.resetForm();
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const handleClose = () => {
    setOpenTicketFormEditDialog(false);
  };

  const { handleSubmit, isSubmitting } = formik;

  return (
    <Dialog open={openTicketFormEditDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>Edit Ticket</DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Box sx={{ mt: 1 }}>
              <TicketFormBasic formik={formik} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
